import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { CustomerOption } from "~/components/select/SelectCustomer";
import { OrderEntryUnit, OrderType } from "~/gql/graphqlBase.generated";
import { CustomerFragment } from "~/gql/queries/customer.generated";

export type CreateOrderMode = "existing" | "new";

export interface CreateOrderDraftLineItem {
	availableQuantity: number;
	cost: number;
	itemId: string;
	itemName: string;
	price: number;
	priceLoading: boolean;
	quantity: number;
	shippingConfigCategoryCode?: string;
	shippingUomId?: string;
	sku: string;
	uniqueKey: string;
	upc: string;
}

export interface DraftItemShippingUomResolution {
	shippingConfigCategoryCode?: string;
	shippingUomId?: string;
}

export interface CreateOrderExistingOrder {
	customerId: string;
	customerLocationId: string;
	customerName: string;
	orderEntryUnit: OrderEntryUnit;
	orderId: string;
	orderNumber: string;
}

export interface CreateOrderState {
	customer: CustomerOption | null;
	customerData: CustomerFragment | null;
	draftItems: CreateOrderDraftLineItem[];
	existingOrder: CreateOrderExistingOrder | null;
	expectedDeliveryDate: string;
	mode: CreateOrderMode;
	openOrderInput: string;
	orderType: OrderType;
	poNumber: string;
	submitting: boolean;
}

const initialState: CreateOrderState = {
	customer: null,
	customerData: null,
	draftItems: [],
	existingOrder: null,
	expectedDeliveryDate: "",
	mode: "new",
	openOrderInput: "",
	orderType: OrderType.Po,
	poNumber: "",
	submitting: false
};

export const createOrderSlice = createSlice({
	initialState,
	name: "createOrder",
	reducers: {
		removeDraftItem: (state, action: PayloadAction<string>) => {
			state.draftItems = state.draftItems.filter((d) => d.uniqueKey !== action.payload);
		},
		resetCreateOrder: () => initialState,
		setCreateOrderCustomer: (state, action: PayloadAction<CustomerOption | null>) => {
			state.customer = action.payload;
			state.customerData = null;
			state.existingOrder = null;
			state.openOrderInput = "";
		},
		setCreateOrderCustomerData: (state, action: PayloadAction<CustomerFragment | null>) => {
			state.customerData = action.payload;
		},
		setCreateOrderDraftItems: (state, action: PayloadAction<CreateOrderDraftLineItem[]>) => {
			state.draftItems = action.payload;
		},
		setCreateOrderExistingOrder: (state, action: PayloadAction<CreateOrderExistingOrder | null>) => {
			state.existingOrder = action.payload;
		},
		setCreateOrderExpectedDeliveryDate: (state, action: PayloadAction<string>) => {
			state.expectedDeliveryDate = action.payload;
		},
		setCreateOrderMode: (state, action: PayloadAction<CreateOrderMode>) => {
			state.mode = action.payload;
		},
		setCreateOrderOpenOrderInput: (state, action: PayloadAction<string>) => {
			state.openOrderInput = action.payload;
		},
		setCreateOrderOrderType: (state, action: PayloadAction<OrderType>) => {
			state.orderType = action.payload;
		},
		setCreateOrderPoNumber: (state, action: PayloadAction<string>) => {
			state.poNumber = action.payload;
		},
		setCreateOrderSubmitting: (state, action: PayloadAction<boolean>) => {
			state.submitting = action.payload;
		},
		setDraftItemPrice: (state, action: PayloadAction<{ price: number; uniqueKey: string }>) => {
			const item = state.draftItems.find((d) => d.uniqueKey === action.payload.uniqueKey);
			if (item) item.price = action.payload.price;
		},
		setDraftItemPricesFromMap: (state, action: PayloadAction<Record<string, number>>) => {
			for (const item of state.draftItems) {
				if (action.payload[item.itemId] !== undefined) {
					item.price = action.payload[item.itemId];
				}
				item.priceLoading = false;
			}
		},
		setDraftItemQuantity: (state, action: PayloadAction<{ quantity: number; uniqueKey: string }>) => {
			const item = state.draftItems.find((d) => d.uniqueKey === action.payload.uniqueKey);
			if (item) item.quantity = action.payload.quantity;
		},
		setDraftItemsPriceLoading: (state, action: PayloadAction<boolean>) => {
			for (const item of state.draftItems) item.priceLoading = action.payload;
		},
		setDraftItemsShippingUomByItemId: (
			state,
			action: PayloadAction<Record<string, DraftItemShippingUomResolution>>
		) => {
			for (const item of state.draftItems) {
				const resolved = action.payload[item.itemId];
				if (!resolved) continue;
				item.shippingConfigCategoryCode = resolved.shippingConfigCategoryCode;
				item.shippingUomId = resolved.shippingUomId;
			}
		}
	}
});

export const {
	removeDraftItem,
	resetCreateOrder,
	setCreateOrderCustomer,
	setCreateOrderCustomerData,
	setCreateOrderDraftItems,
	setCreateOrderExistingOrder,
	setCreateOrderExpectedDeliveryDate,
	setCreateOrderMode,
	setCreateOrderOpenOrderInput,
	setCreateOrderOrderType,
	setCreateOrderPoNumber,
	setCreateOrderSubmitting,
	setDraftItemPrice,
	setDraftItemPricesFromMap,
	setDraftItemQuantity,
	setDraftItemsPriceLoading,
	setDraftItemsShippingUomByItemId
} = createOrderSlice.actions;

export default createOrderSlice.reducer;
